import Blog16Icon from '@atlaskit/icon-object/glyph/blog/16';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { messages } from '@databases/intl/messages';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { useResolvedReference } from '@databases/redux/resolved-reference';
import {
  BlogpostReferenceOrContentId,
  Content,
  CreateOrResolvedPageReference,
  OptionalCreatePageReferenceOrContentId,
  ResolvedBlogpostReferenceOrContentId,
  ResolvedPageReferenceOrContentId,
} from '@databases/types/confluence';
import {
  blogpostToReference,
  isContent,
  isPageReference,
  pageToReference,
} from '@databases/utils/confluence/utils';
import {
  selectContentByIdOptional,
  selectContentByIdStatus,
  selectContentRefOptional,
  selectContentRefStatus,
} from '@databases/utils/redux/content/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import React, { FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import AbstractLink from './AbstractLink';

type ContentRef =
  | CreateOrResolvedPageReference
  | BlogpostReferenceOrContentId
  | Content<'page'>
  | Content<'blogpost'>;

export function selectResolvedContentRef(
  state: DefaultRootState,
  { contentRef }: { contentRef: ContentRef }
): ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId | undefined {
  if (isContent(contentRef)) {
    return contentRef.type === 'blogpost'
      ? blogpostToReference(contentRef)
      : pageToReference(contentRef);
  } else if (
    !('create' in contentRef) ||
    ('create' in contentRef && !contentRef.create)
  ) {
    return selectContentRefOptional(state, { contentRef });
  }
}

function selectResolvedContentRefStatus(
  state: DefaultRootState,
  { contentRef }: { contentRef: ContentRef }
): AsyncOperationStatus | undefined {
  if (
    isContent(contentRef) ||
    (isPageReference(contentRef) && 'create' in contentRef && contentRef.create)
  ) {
    return AsyncOperationStatus.SUCCEEDED;
  } else {
    return selectContentRefStatus(state, { contentRef })?.status;
  }
}

function selectContent(
  state: DefaultRootState,
  { contentRef }: { contentRef: ContentRef }
): Content | undefined {
  if (isContent(contentRef)) {
    return contentRef;
  } else {
    const resolvedContentRef = selectResolvedContentRef(state, { contentRef });
    return (
      resolvedContentRef &&
      selectContentByIdOptional(state, { contentId: resolvedContentRef.contentId })
    );
  }
}

function selectContentStatus(
  state: DefaultRootState,
  { contentRef }: { contentRef: ContentRef }
): AsyncOperationStatus | undefined {
  if (isContent(contentRef)) {
    return AsyncOperationStatus.SUCCEEDED;
  } else {
    const resolvedContentRef = selectResolvedContentRef(state, { contentRef });
    return (
      resolvedContentRef &&
      selectContentByIdStatus(state, { contentId: resolvedContentRef.contentId })?.status
    );
  }
}

function selectContentLinkStatus(
  state: DefaultRootState,
  { contentRef }: { contentRef: ContentRef }
): 'archived' | 'deleted' | 'restricted' | 'create' | undefined {
  const resolvedContentRef = selectResolvedContentRef(state, { contentRef });
  const resolvedContentRefStatus = selectResolvedContentRefStatus(state, { contentRef });
  const content = selectContent(state, { contentRef });
  const contentStatus = selectContentStatus(state, { contentRef });

  if (isPageReference(contentRef) && 'create' in contentRef && contentRef.create) {
    return 'create';
  } else if (content?.status === 'archived') {
    return 'archived';
  } else if (
    contentStatus === AsyncOperationStatus.SUCCEEDED &&
    resolvedContentRef &&
    !content
  ) {
    return 'restricted';
  } else if (
    resolvedContentRefStatus === AsyncOperationStatus.SUCCEEDED &&
    !resolvedContentRef
  ) {
    return 'deleted';
  } else {
    return undefined;
  }
}

export interface ContentLinkProps {
  contentRef:
    | OptionalCreatePageReferenceOrContentId
    | BlogpostReferenceOrContentId
    | Content<'page'>
    | Content<'blogpost'>;
  useSmartLink: boolean;
  noFocus?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
}

/**
 * Renders a formatted placeholder representing a page reference. The page reference is resolved
 * automatically to retrieve the page URL. Until the page reference is resolved, the page title is
 * shown as plain text, once it is resolved, it is converted into a link to the page.
 */
const ContentLink: FunctionComponent<ContentLinkProps> = ({
  contentRef,
  noFocus,
  onClick,
  onFocus,
  useSmartLink,
}) => {
  const resolvedContentRef = useResolvedReference(contentRef);

  const status = defaultStoreUtils.useEqualSelector((state) => {
    return resolvedContentRef
      ? selectContentLinkStatus(state, { contentRef: resolvedContentRef })
      : undefined;
  });

  const { formatMessage } = useIntl();

  const [title, href, type] = useMemo(
    () => [
      resolvedContentRef && 'contentTitle' in resolvedContentRef
        ? resolvedContentRef.contentTitle
        : 'contentTitle' in contentRef
        ? contentRef.contentTitle
        : undefined,
      resolvedContentRef && 'url' in resolvedContentRef
        ? resolvedContentRef.url
        : undefined,
      resolvedContentRef && 'type' in resolvedContentRef
        ? resolvedContentRef.type
        : 'type' in contentRef
        ? contentRef.type
        : undefined,
    ],
    [contentRef, resolvedContentRef]
  );

  return (
    <AbstractLink
      href={href}
      onClick={onClick}
      onFocus={onFocus}
      noFocus={noFocus}
      useSmartLink={useSmartLink}
      iconBefore={
        type ? (
          type === 'blogpost' ? (
            <Blog16Icon label="" />
          ) : (
            <Page16Icon label="" />
          )
        ) : (
          <Spinner size="xsmall" />
        )
      }
      iconAfter={
        status && (
          <Lozenge
            appearance={
              status === 'deleted'
                ? 'removed'
                : status === 'restricted'
                ? 'moved'
                : status === 'create'
                ? 'new'
                : undefined
            }
          >
            {status}
          </Lozenge>
        )
      }
    >
      {title || formatMessage(messages['link-field.content-link-loading-text'])}
    </AbstractLink>
  );
};

export default ContentLink;
